import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, ShieldCheck, RefreshCw, Boxes, AlertCircle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [feeds, setFeeds] = useState(null);
  const loadSummary = () => api.get("/marketplace/admin/dashboard/summary").then((r) => setS(r.data)).catch(() => setS(null));
  useEffect(() => {
    loadSummary();
    api.get("/marketplace/admin/refresh-status").then((r) => setLastRefresh(r.data.at)).catch(() => {});
    api.get("/admin/product-feeds").then((r) => setFeeds(r.data.feeds || [])).catch(() => setFeeds([]));
  }, []);
  const money = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/marketplace/admin/refresh-all");
      await loadSummary();
      setLastRefresh(r.data.at);
      const syncedTxt = r.data.synced?.length ? `Synced: ${r.data.synced.join(", ")}` : "No external providers connected — live figures refreshed";
      toast.success("Provider data refreshed", { description: syncedTxt });
    } catch (e) { toast.error("Refresh failed — please try again"); } finally { setRefreshing(false); }
  };

  const CARDS = [
    { label: "Total Customers", val: s?.total_customers, to: "/admin/customers" },
    { label: "Total Sellers", val: s?.total_sellers, to: "/admin/storefronts" },
    { label: "Pending Seller Approvals", val: s?.pending_seller_approvals, to: "/admin/seller-verification", alert: true },
    { label: "Active Products", val: s?.active_products, to: "/admin/products" },
    { label: "Pending Product Approvals", val: s?.pending_product_approvals, to: "/admin/products", alert: true },
    { label: "Orders Today", val: s?.orders_today, to: "/admin/orders" },
    { label: "Orders This Month", val: s?.orders_month, to: "/admin/orders" },
    { label: "Marketplace GMV", val: money(s?.gmv), to: "/admin/orders" },
    { label: "Commission", val: money(s?.commission), to: "/admin/commission" },
    { label: "Pending Settlements", val: money(s?.pending_settlements), to: "/admin/settlements" },
    { label: "Pending Returns", val: s?.pending_returns, to: "/admin/refunds" },
    { label: "Pending Refunds", val: s?.pending_refunds, to: "/admin/refunds" },
    { label: "Active Promotions", val: s?.active_promotions, to: "/admin/promotions" },
    { label: "Reported Reviews", val: s?.reported_reviews, to: "/admin/reviews", alert: true },
    { label: "Compliance Alerts", val: s?.compliance_alerts, to: "/admin/seller-verification", alert: true },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Marketplace Control Center" icon={LayoutDashboard} subtitle="Live overview of the Suffle marketplace. All figures are real, no fabricated data." />
        <div className="flex flex-col items-end gap-1">
          <button onClick={refreshAll} disabled={refreshing} data-testid="global-refresh-btn" className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Refreshing…" : "Refresh all data"}
          </button>
          <span className="text-[11px] text-slate-400" data-testid="last-refresh-at">{lastRefresh ? `Last refreshed ${new Date(lastRefresh).toLocaleString()}` : "Not refreshed yet"}</span>
        </div>
      </div>
      {s && (
        <button onClick={() => nav("/admin/marketplace")} data-testid="backup-health-badge"
          className={`mb-4 flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition hover:shadow-sm ${s.backup_restore_ok ? "border-emerald-200 bg-emerald-50" : s.backup_last_at ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"}`}>
          <ShieldCheck className={`h-5 w-5 ${s.backup_restore_ok ? "text-emerald-600" : s.backup_last_at ? "text-rose-600" : "text-amber-600"}`} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">Nightly backups {s.backup_last_at ? (s.backup_restore_ok ? "healthy" : "— restore test FAILED") : "not run yet"}</p>
            <p className="text-xs text-slate-500">{s.backup_last_at ? `Last backup ${new Date(s.backup_last_at).toLocaleString()} · ${s.backup_count} kept · restore test ${s.backup_restore_ok ? "passed ✓" : "failed ✗"}` : "Run your first backup in Marketplace → Data Backups"}</p>
          </div>
          <span className="text-xs font-semibold text-slate-400">Manage →</span>
        </button>
      )}
      {feeds && feeds.length > 0 && (
        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="feed-health-card">
          <button onClick={() => nav("/admin/product-feeds")} data-testid="feed-health-manage" className="mb-2 flex w-full items-center gap-2 text-left">
            <Boxes className="h-5 w-5 text-indigo-500" />
            <span className="text-sm font-semibold text-slate-800">Product feeds</span>
            <span className="text-xs text-slate-400">{feeds.filter((f) => f.active).length}/{feeds.length} active · {feeds.reduce((a, f) => a + (f.live_count || 0), 0)} live products</span>
            <span className="ml-auto text-xs font-semibold text-slate-400">Manage →</span>
          </button>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {feeds.map((f) => {
              const ok = f.last_status === "ok"; const err = f.last_status && f.last_status !== "ok";
              return (
                <div key={f.id} data-testid={`feed-health-${f.id}`} className={`flex items-start gap-2 rounded-xl border p-2.5 ${err ? "border-rose-200 bg-rose-50" : ok ? "border-emerald-100 bg-emerald-50/50" : "border-slate-200 bg-slate-50"}`}>
                  {err ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" /> : ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : <Boxes className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800">{f.merchant}{!f.active && <span className="ml-1 text-xs font-normal text-slate-400">(off)</span>}</p>
                    <p className="text-xs text-slate-500">{f.live_count || 0} products · {f.last_sync_at ? `synced ${new Date(f.last_sync_at).toLocaleString()}` : "never synced"}</p>
                    {err && <p className="truncate text-xs font-medium text-rose-600" title={f.last_status}>{f.last_status}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {CARDS.map((c) => (
          <button key={c.label} onClick={() => nav(c.to)} data-testid={`admin-stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`rounded-2xl border bg-white p-4 text-left transition hover:shadow-md ${c.alert && s && Number(c.val) > 0 ? "border-amber-200" : "border-slate-200"}`}>
            <p className="text-xl font-extrabold text-slate-900">{s ? (c.val ?? 0) : "—"}</p>
            <p className="text-xs text-slate-500">{c.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
